<script>
    import { onMount } from 'svelte';
    import { t } from 'svelte-i18n';
    import { 
        fetchCotizaCabs, 
        saveCotizaCab as saveCotizaCabController, 
        removeCotizaCab, 
        searchCotizaCabs as searchCotizaCabsController, 
        subscribeToCotizaCabs, 
        unsubscribeFromCotizaCabs,
        generateCotizaNumber
    } from '../controllers/cotizaCabController.js';
    import { 
        fetchCotizaDets, 
        saveCotizaDet as saveCotizaDetController, 
        removeCotizaDet, 
        searchCotizaDets, 
        subscribeToCotizaDets, 
        unsubscribeFromCotizaDets,
        updateCotizaCabTotal
    } from '../controllers/cotizaDetController.js';
    import { fetchProductos } from '../controllers/productoController.js';
    import Swal from 'sweetalert2';

    // Estado de la aplicación
    let cotizaCabs = [];
    let cotizaDets = [];
    let productos = [];
    let selectedCotizaCab = null;
    let showDetails = false;
    let loading = false;
    let searchQuery = '';
    let currentPage = 1;
    let totalRecords = 0;
    let perPage = 10;

    // Formulario de nueva cotización
    let newCotizaCab = {
        numero: '',
        fecha: new Date().toISOString().split('T')[0],
        nit: '',
        nombre: '',
        telefono: '',
        correo: '',
        total: 0
    };

    // Formulario de nuevo detalle
    let newCotizaDet = {
        cotiza_cab: null,
        producto: null,
        cantidad: 1,
        precio: 0,
        total: 0
    };

    // Cargar datos iniciales
    onMount(async () => {
        await loadCotizaCabs();
        await loadProductos();
        subscribeToCotizaCabs(searchQuery, loadCotizaCabs);
    });

    // Cargar cotizaciones
    async function loadCotizaCabs() {
        loading = true;
        try {
            const response = await fetchCotizaCabs(currentPage, perPage);
            if (response) {
                cotizaCabs = response.cotizaCab;
                totalRecords = response.totalRecords;
            }
        } catch (error) {
            console.error('Error loading cotiza cabs:', error);
        }
        loading = false;
    }

    // Cargar productos
    async function loadProductos() {
        try {
            const response = await fetchProductos(1, 1000);
            if (response) {
                productos = response.producto;
            }
        } catch (error) {
            console.error('Error loading productos:', error);
        }
    }

    // Cargar detalles de una cotización
    async function loadCotizaDets(cotizaCabId) {
        try {
            const response = await fetchCotizaDets(cotizaCabId, 1, 1000);
            if (response) {
                cotizaDets = response.cotizaDet;
            }
        } catch (error) {
            console.error('Error loading cotiza dets:', error);
        }
    }

    // Seleccionar cotización y cargar detalles
    async function selectCotizaCab(cotizaCab) {
        selectedCotizaCab = cotizaCab;
        showDetails = true;
        await loadCotizaDets(cotizaCab.id);
        newCotizaDet.cotiza_cab = cotizaCab.id;
    }

    // Generar número de cotización
    async function generateNumber() {
        try {
            const numero = await generateCotizaNumber();
            newCotizaCab.numero = numero;
        } catch (error) {
            console.error('Error generating cotiza number:', error);
        }
    }

    // Guardar cotización
    async function saveCotizaCab() {
        try {
            await saveCotizaCabController(newCotizaCab, null);
            await loadCotizaCabs();
            resetNewCotizaCab();
        } catch (error) {
            console.error('Error saving cotiza cab:', error);
        }
    }

    // Guardar detalle
    async function saveCotizaDet() {
        try {
            newCotizaDet.total = newCotizaDet.cantidad * newCotizaDet.precio;
            await saveCotizaDetController(newCotizaDet, null);
            await loadCotizaDets(selectedCotizaCab.id);
            await updateCotizaCabTotal(selectedCotizaCab.id);
            resetNewCotizaDet();
        } catch (error) {
            console.error('Error saving cotiza det:', error);
        }
    }

    // Eliminar cotización
    async function deleteCotizaCab(cotizaCab) {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción eliminará la cotización y todos sus detalles.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await removeCotizaCab(cotizaCab.id);
                await loadCotizaCabs();
                if (selectedCotizaCab && selectedCotizaCab.id === cotizaCab.id) {
                    selectedCotizaCab = null;
                    showDetails = false;
                    cotizaDets = [];
                }
            } catch (error) {
                console.error('Error deleting cotiza cab:', error);
            }
        }
    }

    // Eliminar detalle
    async function deleteCotizaDet(cotizaDet) {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción eliminará el detalle de la cotización.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await removeCotizaDet(cotizaDet.id);
                await loadCotizaDets(selectedCotizaCab.id);
                await updateCotizaCabTotal(selectedCotizaCab.id);
            } catch (error) {
                console.error('Error deleting cotiza det:', error);
            }
        }
    }

    // Buscar cotizaciones
    async function searchCotizaCabs() {
        loading = true;
        try {
            const response = await searchCotizaCabsController(searchQuery, currentPage, perPage);
            if (response) {
                cotizaCabs = response.cotizaCab;
                totalRecords = response.totalRecords;
            }
        } catch (error) {
            console.error('Error searching cotiza cabs:', error);
        }
        loading = false;
    }

    // Resetear formularios
    function resetNewCotizaCab() {
        newCotizaCab = {
            numero: '',
            fecha: new Date().toISOString().split('T')[0],
            nit: '',
            nombre: '',
            telefono: '',
            correo: '',
            total: 0
        };
        generateNumber();
    }

    function resetNewCotizaDet() {
        newCotizaDet = {
            cotiza_cab: selectedCotizaCab?.id || null,
            producto: null,
            cantidad: 1,
            precio: 0,
            total: 0
        };
    }

    // Calcular total de la cotización
    function calculateTotal() {
        return cotizaDets.reduce((sum, det) => sum + (det.total || 0), 0);
    }
</script>

<div class="container-fluid">
    <div class="row">
        <!-- Lista de Cotizaciones -->
        <div class="col-md-6">
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5 class="mb-0">Cotizaciones</h5>
                    <button class="btn btn-primary btn-sm" on:click={generateNumber}>
                        <i class="fas fa-plus"></i> Nueva Cotización
                    </button>
                </div>
                <div class="card-body">
                    <!-- Búsqueda -->
                    <div class="mb-3">
                        <div class="input-group">
                            <input 
                                type="text" 
                                class="form-control" 
                                placeholder="Buscar por número, cliente o NIT..."
                                bind:value={searchQuery}
                                on:keyup={(e) => e.key === 'Enter' && searchCotizaCabs()}
                            >
                            <button class="btn btn-outline-secondary" on:click={searchCotizaCabs}>
                                <i class="fas fa-search"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Lista de cotizaciones -->
                    <div class="table-responsive">
                        <table class="table table-hover">
                            <thead>
                                <tr>
                                    <th>Número</th>
                                    <th>Fecha</th>
                                    <th>Cliente</th>
                                    <th>Total</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each cotizaCabs as cotizaCab}
                                    <tr class="cursor-pointer" on:click={() => selectCotizaCab(cotizaCab)}>
                                        <td>{cotizaCab.numero}</td>
                                        <td>{cotizaCab.fecha}</td>
                                        <td>{cotizaCab.nombre}</td>
                                        <td>${cotizaCab.total?.toFixed(2) || '0.00'}</td>
                                        <td>
                                            <button class="btn btn-sm btn-danger" on:click|stopPropagation={() => deleteCotizaCab(cotizaCab)}>
                                                <i class="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Detalles de Cotización -->
        <div class="col-md-6">
            {#if showDetails && selectedCotizaCab}
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">Detalles - {selectedCotizaCab.numero}</h5>
                        <button class="btn btn-sm btn-secondary" on:click={() => { showDetails = false; selectedCotizaCab = null; }}>
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                    <div class="card-body">
                        <!-- Información del cliente -->
                        <div class="mb-3 p-3 bg-light rounded">
                            <h6><strong>Cliente:</strong> {selectedCotizaCab.nombre}</h6>
                            <p class="mb-1"><strong>NIT:</strong> {selectedCotizaCab.nit}</p>
                            <p class="mb-1"><strong>Teléfono:</strong> {selectedCotizaCab.telefono}</p>
                            <p class="mb-0"><strong>Correo:</strong> {selectedCotizaCab.correo}</p>
                        </div>

                        <!-- Lista de detalles -->
                        <div class="table-responsive mb-3">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Producto</th>
                                        <th>Cantidad</th>
                                        <th>Precio</th>
                                        <th>Total</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {#each cotizaDets as det}
                                        <tr>
                                            <td>{det.producto?.nombre || 'N/A'}</td>
                                            <td>{det.cantidad}</td>
                                            <td>${det.precio?.toFixed(2) || '0.00'}</td>
                                            <td>${det.total?.toFixed(2) || '0.00'}</td>
                                            <td>
                                                <button class="btn btn-sm btn-danger" on:click={() => deleteCotizaDet(det)}>
                                                    <i class="fas fa-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    {/each}
                                </tbody>
                            </table>
                        </div>

                        <!-- Total -->
                        <div class="alert alert-info">
                            <h6 class="mb-0">Total: ${calculateTotal().toFixed(2)}</h6>
                        </div>

                        <!-- Formulario para agregar detalle -->
                        <div class="card">
                            <div class="card-header">
                                <h6 class="mb-0">Agregar Producto</h6>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="col-md-6">
                                        <label for="producto-select" class="form-label">Producto</label>
                                        <select id="producto-select" class="form-select" bind:value={newCotizaDet.producto}>
                                            <option value="">Seleccionar producto</option>
                                            {#each productos as producto}
                                                <option value={producto.id}>{producto.nombre}</option>
                                            {/each}
                                        </select>
                                    </div>
                                    <div class="col-md-3">
                                        <label for="cantidad-input" class="form-label">Cantidad</label>
                                        <input id="cantidad-input" type="number" class="form-control" bind:value={newCotizaDet.cantidad} min="1">
                                    </div>
                                    <div class="col-md-3">
                                        <label for="precio-input" class="form-label">Precio</label>
                                        <input id="precio-input" type="number" class="form-control" bind:value={newCotizaDet.precio} min="0" step="0.01">
                                    </div>
                                </div>
                                <div class="mt-3">
                                    <button class="btn btn-primary btn-sm" on:click={saveCotizaDet}>
                                        <i class="fas fa-plus"></i> Agregar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            {:else}
                <div class="card">
                    <div class="card-body text-center">
                        <i class="fas fa-file-invoice fa-3x text-muted mb-3"></i>
                        <h5 class="text-muted">Selecciona una cotización para ver sus detalles</h5>
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>

<style>
    .cursor-pointer {
        cursor: pointer;
    }
    .cursor-pointer:hover {
        background-color: #f8f9fa;
    }
</style>
